import { AutomationProject } from "@/types/automation";
import { apiVersions, armEndpoint, getLogicEndpoint } from "./endpoints";

export type AppNameValidationResult = {
  isValid: boolean;
  error: string;
};

/**
 * Gets an automation project.
 */
export const getAutomationProject = async (projectName: string, getToken: () => Promise<string | null>): Promise<AutomationProject> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get Otto token');
  }

  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}?api-version=${apiVersions.otto}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorResponse = errorText ? JSON.parse(errorText) : null;
      const error = errorResponse?.error ?? errorResponse;
      errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
    } catch {
      errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
    }
    console.error('GET request failed:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  const project = await response.json();
  return project;
};

/**
 * Gets a list of automation projects in a subscription.
 * Uses the ARM API and navigates through all nextLinks to get the complete list.
 * 
 * @param subscriptionId - The Azure subscription ID
 * @param getToken - Function to get the ARM access token
 * @returns Array of automation project summaries with name, id, location, and status
 */
export type AutomationProjectSummary = {
  name: string;
  id: string;
  location: string;
  status: string;
};

export const listAutomationProjects = async (
  subscriptionId: string,
  getToken: () => Promise<string | null>
): Promise<AutomationProjectSummary[]> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get ARM token');
  }

  const projects: AutomationProjectSummary[] = [];
  let nextLink: string | null = `${armEndpoint}/subscriptions/${subscriptionId}/providers/Microsoft.Logic/automationProjects?api-version=${apiVersions.otto}`;

  while (nextLink) {
    const response = await fetch(nextLink, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      try {
        const errorResponse = errorText ? JSON.parse(errorText) : null;
        const error = errorResponse?.error ?? errorResponse;
        errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
      } catch {
        errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
      }
      console.error('Failed to list automation projects:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    type ProjectResponse = { name: string; id: string; location: string; properties?: { provisioningState?: string } };
    const data: { value?: ProjectResponse[]; nextLink?: string } = await response.json();
    const pageProjects = (data.value ?? []).map((project) => ({
      name: project.name,
      id: project.id,
      location: project.location,
      status: project.properties?.provisioningState ?? 'Unknown',
    }));

    projects.push(...pageProjects);
    nextLink = data.nextLink ?? null;
  }

  return projects;
};

/**
 * Creates an automation project in a subscription using the ARM API.
 * 
 * @param subscriptionId - The Azure subscription ID
 * @param resourceGroup - The Azure resource group name
 * @param name - The name of the automation project to create
 * @param location - The Azure region for the project (e.g., 'eastus')
 * @param getToken - Function to get the ARM access token
 * @returns The created automation project resource
 */
export type CreateAutomationProjectResult = {
  id: string;
  name: string;
  type: string;
  location: string;
  properties?: {
    provisioningState?: string;
  };
};

export const createAutomationProject = async (
  subscriptionId: string,
  resourceGroup: string,
  name: string,
  location: string,
  getToken: () => Promise<string | null>
): Promise<CreateAutomationProjectResult> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get ARM token');
  }

  const resourceId = `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.Logic/automationProjects/${name}`;
  const url = `${armEndpoint}${resourceId}?api-version=${apiVersions.otto}`;

  const payload = {
    id: resourceId,
    name,
    type: 'Microsoft.Logic/automationProjects',
    location,
    identity: { type: 'SystemAssigned' },
    properties: { description: `Automation Project - ${name}` }
  };

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorResponse = errorText ? JSON.parse(errorText) : null;
      const error = errorResponse?.error ?? errorResponse;
      errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
    } catch {
      errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
    }
    console.error('Failed to create automation project:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * Validates an application name.
 * @param appName - The app name to validate
 * @param existingAppNames - Array of existing app names to check for duplicates
 * @returns Validation result with isValid flag and error message
 */
export const validateAppName = (appName: string, existingAppNames: string[]): AppNameValidationResult => {
  const trimmed = appName.trim();
  if (!trimmed) {
    return { isValid: false, error: '' }; // Empty - no error message shown yet
  }
  // Check format: alphanumeric, dashes, underscores, 1-64 chars
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
  if (!validNamePattern.test(trimmed)) {
    return { 
      isValid: false, 
      error: 'Name must start with a letter and contain only letters, numbers, dashes, or underscores (max 64 chars)' 
    };
  }
  // Check for duplicate names
  const existingNamesLower = existingAppNames.map(name => name.toLowerCase());
  if (existingNamesLower.includes(trimmed.toLowerCase())) {
    return { isValid: false, error: `An application named "${trimmed}" already exists` };
  }
  return { isValid: true, error: '' };
};

export type CreateAutomationAppResult = {
  resource: unknown;
  statusUrl: string | null;
};

export const createAutomationApp = async (projectName: string, appName: string, _location: string, getToken: () => Promise<string | null>): Promise<CreateAutomationAppResult | undefined> => {
    const token = await getToken();
    if (!token) {
      console.error('Failed to get Otto token');
      return;
    }

    const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}?api-version=${apiVersions.otto}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      try {
        const errorResponse = errorText ? JSON.parse(errorText) : null;
        const error = errorResponse?.error ?? errorResponse;
        errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
      } catch {
        errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
      }
      console.error('PUT request failed:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    const resource = await response.json();
    const statusUrl = response.headers.get('Azure-Asyncoperation');
    
    console.log('PUT request succeeded:', resource);
    console.log('Status URL:', statusUrl);
    
    return { resource, statusUrl };
};

export const deleteAutomationApp = async (projectName: string, appName: string, getToken: () => Promise<string | null>): Promise<boolean> => {
  try {
    const token = await getToken();
    if (!token) {
      console.error('Failed to get Otto token');
      return false;
    }

    const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}?api-version=${apiVersions.otto}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('DELETE request failed:', response.status, errorText);
      return false;
    }

    console.log('DELETE request succeeded');
    return true;
  } catch (error) {
    console.error('Error making DELETE request:', error);
    return false;
  }
};
